/**
 * Unified Account (v3) の期間制約に合わせて、要求期間を分割取得する。
 *
 * v3 の入出金 API は 30 日を超える範囲を
 * `00001 startTime and endTime interval cannot be greater than 30 days` で拒否する。
 * 一方 Classic (v2) は 90 日まで許容するため、config の range を狭めると
 * Classic 利用者のリクエスト数が無駄に増える。そこで v3 側だけここで窓を分割する。
 */

/** UTA API が 1 リクエストで受け付ける最大期間。 */
const UNIFIED_MAX_RANGE_MS = 30 * 24 * 60 * 60 * 1000

/**
 * 要求期間を 30 日以下の窓へ分割する。
 * @param params - from: 開始時刻、to: 終了時刻。
 * @returns 開始が古い順に並んだ窓の配列。
 */
const splitIntoWindows = ({
  from,
  to,
}: {
  readonly from: number
  readonly to: number
}): { readonly startTime: number; readonly endTime: number }[] => {
  const windows: { readonly startTime: number; readonly endTime: number }[] = []
  for (
    let startTime = from;
    startTime <= to;
    startTime += UNIFIED_MAX_RANGE_MS
  ) {
    windows.push({
      startTime,
      endTime: Math.min(startTime + UNIFIED_MAX_RANGE_MS - 1, to),
    })
  }
  return windows
}

/**
 * 30 日窓へ分割して取得し、結果を結合して返す。
 *
 * 呼び出し元は返り値の件数を `config.limit` と比較して「取り切ったか」を判定するため、
 * limit に達した窓が現れた時点で打ち切る。残りの期間は呼び出し元が
 * `fetchedFrom` / `fetchedTo` を元に次周回で再取得する。
 * 窓の処理順は `order` に合わせる (desc なら新しい窓から) 。
 *
 * @param params - from/to: 要求期間、limit: 1 リクエストの上限件数、
 *   order: 取得順、fetchWindow: 1 窓分を取得する関数。
 * @returns 取得できたレコードの配列。
 */
export const fetchUnifiedRecordsInWindows = async <TRow>({
  from,
  to,
  limit,
  order,
  fetchWindow,
}: {
  readonly from: number
  readonly to: number
  readonly limit: number
  readonly order: 'asc' | 'desc'
  readonly fetchWindow: (params: {
    readonly startTime: number
    readonly endTime: number
  }) => Promise<readonly TRow[]>
}): Promise<TRow[]> => {
  const windows = splitIntoWindows({ from, to })
  if (order === 'desc') {
    windows.reverse()
  }

  const rows: TRow[] = []
  for (const window of windows) {
    const fetched = await fetchWindow(window)
    rows.push(...fetched)
    // この窓で上限に達した = 取り切れていないため、以降は次周回に委ねる
    if (fetched.length >= limit) {
      break
    }
  }
  return rows
}
